import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Download, Smartphone, Check } from "lucide-react";
import cutPreviewsLogo from "@/assets/cut-previews-logo.png";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const Install = () => {
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      setIsInstalled(true);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-secondary p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <img 
              src={cutPreviewsLogo} 
              alt="Cut Previews" 
              className="h-24 w-auto"
              width="153"
              height="96"
              fetchPriority="high"
            />
          </div>
          <CardTitle className="text-2xl text-center">
            {isInstalled ? "App Installed!" : "Install Cut Previews"}
          </CardTitle>
          <CardDescription className="text-center">
            {isInstalled 
              ? "You can now use Cut Previews from your home screen"
              : "Add Cut Previews to your home screen for quick access"
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {isInstalled ? (
            <div className="space-y-4">
              <div className="flex items-center justify-center text-primary">
                <Check className="h-16 w-16" />
              </div>
              <Button onClick={() => navigate("/")} className="w-full">
                Go to App
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Smartphone className="h-6 w-6 text-primary mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Works Offline</h3>
                    <p className="text-sm text-muted-foreground">
                      Access your hairstyles anytime, even without internet
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Download className="h-6 w-6 text-primary mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Fast & Lightweight</h3>
                    <p className="text-sm text-muted-foreground">
                      Instant loading, no app store required
                    </p>
                  </div>
                </div>
              </div>

              {deferredPrompt ? (
                <Button onClick={handleInstallClick} className="w-full" size="lg">
                  <Download className="mr-2 h-5 w-5" />
                  Install App
                </Button>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-center text-muted-foreground">
                    To install this app:
                  </p>
                  <div className="space-y-2 text-sm">
                    <p className="font-semibold">On iPhone/iPad:</p>
                    <p className="text-muted-foreground">
                      Tap the Share button in Safari, then "Add to Home Screen"
                    </p>
                    <p className="font-semibold mt-3">On Android:</p>
                    <p className="text-muted-foreground">
                      Tap the menu (⋮) in Chrome, then "Install app" or "Add to Home Screen"
                    </p>
                  </div>
                </div>
              )}

              <Button 
                onClick={() => navigate("/")} 
                variant="outline" 
                className="w-full"
              >
                Continue in Browser
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Install;